import type { Theme, ThemeScheme } from '@/api/types';

/**
 * Runtime re-skinning.
 *
 * The location config decides what this app looks like and arrives over the network after the bundle has
 * loaded, so branding cannot be baked in at build time. `applyTheme` writes the brand colours onto <html> as
 * custom properties; every brandable token in `index.css` reads them through `var(--brand-x, <default>)`, so
 * one call re-skins the whole app.
 *
 * The API sends twelve Material 3 roles per scheme. The mapping from those roles to this app's tokens lives
 * here rather than on the server, deliberately: "which role paints the page behind the card" is a design
 * decision, and moving it to the wire format would turn every restyle into an API change.
 */

/**
 * Material role → CSS custom property.
 *
 * `surface` covers the raised things (card, footer, chips) and `surfaceContainer` the ground behind them,
 * which is what gives the check screen its depth. `surfaceContainerHigh` is one step up again, for the
 * quantity badges that need to read against both.
 */
function propertiesFor(scheme: ThemeScheme): Record<string, string> {
    return {
        // Raised surfaces and the text on them.
        '--brand-background': scheme.surface,
        '--brand-foreground': scheme.onSurface,
        '--brand-card': scheme.surface,
        '--brand-card-foreground': scheme.onSurface,
        '--brand-popover': scheme.surface,
        '--brand-popover-foreground': scheme.onSurface,

        // The ground behind the card, and secondary text.
        '--brand-muted': scheme.surfaceContainer,
        '--brand-muted-foreground': scheme.onSurfaceVariant,

        // One step above the ground - quantity badges.
        '--brand-accent': scheme.surfaceContainerHigh,
        '--brand-accent-foreground': scheme.onSurface,

        // Tonal surfaces: the table chip, unselected tip buttons.
        '--brand-secondary': scheme.secondaryContainer,
        '--brand-secondary-foreground': scheme.onSecondaryContainer,

        // The brand moment.
        '--brand-primary': scheme.primary,
        '--brand-primary-foreground': scheme.onPrimary,
        '--brand-ring': scheme.primary,

        // Hairlines.
        '--brand-border': scheme.outlineVariant,
        '--brand-input': scheme.outlineVariant,

        // Declines.
        '--brand-destructive': scheme.error,
        '--brand-destructive-foreground': scheme.onError,

        // Tip buttons reuse the tonal pair unselected and the brand pair selected.
        '--brand-tip': scheme.secondaryContainer,
        '--brand-tip-foreground': scheme.onSecondaryContainer,
        '--brand-tip-selected': scheme.primary,
        '--brand-tip-selected-foreground': scheme.onPrimary,
    };
}

/** Every property this module manages, so a reset clears all of them and not just the ones a theme set. */
const MANAGED_PROPERTIES = Object.keys(
    propertiesFor({
        primary: '', onPrimary: '', secondaryContainer: '', onSecondaryContainer: '',
        surface: '', onSurface: '', surfaceContainer: '', surfaceContainerHigh: '',
        onSurfaceVariant: '', outlineVariant: '', error: '', onError: '',
    }),
);

/**
 * Paints the app in a brand's colours.
 *
 * Pass null to clear — every managed property is removed rather than blanked, which is what lets the CSS
 * `var(--brand-x, <default>)` fallbacks engage and restore the stock palette. Writing `''` would leave the
 * variable defined-but-empty and break the fallback chain.
 *
 * A location with no theme, or one the API could not read, arrives here as null. That is the intended path:
 * the app has a complete default palette, and an unbranded-but-coherent screen beats a half-branded one.
 */
export function applyTheme(
    theme: Theme | null | undefined,
    options: { scheme?: 'light' | 'dark'; root?: HTMLElement } = {},
): void {
    const root = options.root ?? document.documentElement;

    if (!theme) {
        for (const property of MANAGED_PROPERTIES) root.style.removeProperty(property);
        syncThemeColor(null);
        return;
    }

    const scheme = options.scheme === 'dark' ? theme.dark : theme.light;

    for (const [property, value] of Object.entries(propertiesFor(scheme))) {
        if (value) root.style.setProperty(property, value);
        else root.style.removeProperty(property);
    }

    syncThemeColor(scheme.surfaceContainer);
}

/** The stock ground, matching the `--brand-muted` fallback in `index.css`. Used when no theme is applied. */
const DEFAULT_THEME_COLOR = '#FFFFFF';

/**
 * Points the browser's own chrome at the brand.
 *
 * On a phone this is the strip above and below the page — the status bar area on iOS, the toolbar on Android.
 * Left at the static white in `index.html`, a location with a dark or saturated ground renders as a branded
 * page in a white frame, which reads as a page that failed to load its background rather than as a design.
 *
 * Matches `surfaceContainer` because that is what `bg-muted` paints, and `bg-muted` is what sits behind the
 * top of every screen.
 */
function syncThemeColor(color: string | null): void {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;

    /*
     * Themes arrive as `#RRGGBBAA` — the CSS custom properties take that happily, but `theme-color` does not
     * accept alpha across browsers and a rejected value leaves the previous colour in place. Material's
     * surfaces are opaque anyway, so dropping the alpha loses nothing and is accepted everywhere.
     */
    const opaque = color && /^#[0-9a-f]{8}$/i.test(color) ? color.slice(0, 7) : color;

    meta.content = opaque || DEFAULT_THEME_COLOR;
}

/* ------------------------------------------------------------------------------------------------------
 * Colour maths.
 *
 * The API now sends `onPrimary` and friends explicitly, chosen by whoever built the theme, so this app no
 * longer computes which ink to use. These remain because they answer a different question: whether a brand's
 * own choices are actually legible. `assertReadableTheme` uses them to complain in development about a theme
 * that would ship unreadable text.
 * ---------------------------------------------------------------------------------------------------- */

interface Rgb {
    r: number;
    g: number;
    b: number;
}

const HEX_PATTERN = /^#?(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Parses `#abc`, `#aabbcc` or `#aabbccff`. Alpha is accepted and ignored — contrast is judged on the RGB. */
export function parseHex(hex: string | null | undefined): Rgb | null {
    if (!hex) return null;

    const trimmed = hex.trim();
    if (!HEX_PATTERN.test(trimmed)) return null;

    let body = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
    if (body.length === 3) body = body[0] + body[0] + body[1] + body[1] + body[2] + body[2];
    if (body.length === 8) body = body.slice(0, 6);

    return {
        r: parseInt(body.slice(0, 2), 16),
        g: parseInt(body.slice(2, 4), 16),
        b: parseInt(body.slice(4, 6), 16),
    };
}

/** WCAG 2.x relative luminance. Channels are linearized before weighting — averaging the raw bytes instead
 *  is the classic shortcut and it misjudges saturated colors badly (pure blue looks mid-bright, it is not). */
export function relativeLuminance({ r, g, b }: Rgb): number {
    const linearize = (channel: number): number => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: Rgb, b: Rgb): number {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** True when the pair clears WCAG AA for normal text. */
export function meetsContrastAA(foreground: string, background: string): boolean {
    const fg = parseHex(foreground);
    const bg = parseHex(background);

    // An unparseable colour is not evidence of a contrast failure; the caller has bigger problems.
    if (!fg || !bg) return true;

    return contrastRatio(fg, bg) >= 4.5;
}

/**
 * Warns, in development only, about brand pairs that would ship unreadable text.
 *
 * A restaurant can upload a theme whose `onPrimary` does not actually read on its `primary`. That is not
 * something this app should silently correct — the brand chose those colours — but it is worth surfacing to
 * whoever is looking at the screen, because the alternative is a guest squinting at a pay button.
 */
export function assertReadableTheme(theme: Theme | null | undefined, brandName: string): void {
    if (!import.meta.env.DEV || !theme) return;

    const pairs: [string, string, string][] = [
        ['onPrimary / primary', theme.light.onPrimary, theme.light.primary],
        ['onSurface / surface', theme.light.onSurface, theme.light.surface],
        ['onSurfaceVariant / surfaceContainer', theme.light.onSurfaceVariant, theme.light.surfaceContainer],
    ];

    for (const [label, foreground, background] of pairs) {
        if (!meetsContrastAA(foreground, background)) {
            console.warn(
                `[theme] ${brandName}: ${label} is below WCAG AA (${foreground} on ${background}). ` +
                    'Guests may struggle to read it.',
            );
        }
    }
}
